import $i18n from '@/locales/i18n';
import React from 'react';
import { CnAsyncSelect, CnNumberPicker, CnCheckbox } from '@cainiaofe/cn-ui';
import dayjs from 'dayjs';
import './flexible-time-selection.scss';
export const calculateActualTime = (timeValue, format) => {
    const now = dayjs();
    const handleFormat = (time, format) => {
        if (time) {
            if (format) {
                return time.format(format);
            }
            else {
                return time?.valueOf();
            }
        }
        return time;
    };
    if (timeValue.mode === 'now') {
        return handleFormat(now, format);
    }
    else if (timeValue.mode === 'relative') {
        if (timeValue.direction === 'subtract') {
            let result = now.subtract(timeValue.amount, timeValue.unit);
            if (timeValue.enableRounding) {
                if (timeValue.roundToStart === 'start') {
                    result = result.startOf(timeValue.unit);
                }
                else {
                    result = result.endOf(timeValue.unit);
                }
            }
            if (timeValue.enableExactTime) {
                result = result
                    .hour(parseInt(timeValue.hour))
                    .minute(parseInt(timeValue.minute))
                    .second(parseInt(timeValue.second));
            }
            return handleFormat(result, format);
        }
        else {
            let result = now.add(timeValue.amount, timeValue.unit);
            if (timeValue.enableRounding) {
                if (timeValue.roundToStart === 'start') {
                    result = result.startOf(timeValue.unit);
                }
                else {
                    result = result.endOf(timeValue.unit);
                }
            }
            if (timeValue.enableExactTime) {
                result = result
                    .hour(parseInt(timeValue.hour))
                    .minute(parseInt(timeValue.minute))
                    .second(parseInt(timeValue.second));
            }
            return handleFormat(result, format);
        }
    }
    else if (timeValue.mode === 'period') {
        const periodMap = {
            day: 'day',
            week: 'week',
            month: 'month',
            quarter: 'quarter',
            year: 'year',
        };
        const periodChar = periodMap[timeValue.period];
        if (timeValue.position === 'now') {
            return handleFormat(now, format);
        }
        else if (timeValue.position === 'start') {
            return handleFormat(now.startOf(periodChar), format);
        }
        else if (timeValue.position === 'end') {
            return handleFormat(now.endOf(periodChar), format);
        }
    }
    return handleFormat(now, format);
};
/**
 * 相对时间选择器组件
 */
const RelativeTimePicker = ({ value = {}, onChange, disabled = false, label = $i18n.get({ id: 'Time', dm: '时间' }), }) => {
    // 默认值结构
    const defaultValue = {
        mode: 'now',
        // 相对时间配置
        direction: 'subtract',
        amount: 1,
        unit: 'h',
        // 时间段配置
        period: 'day',
        position: 'start',
        // 时间调整选项
        enableRounding: false,
        roundToStart: 'start',
        enableExactTime: false,
        hour: '00',
        minute: '00',
        second: '00', // 秒 00-59
    };
    // 合并默认值和传入值
    const currentValue = { ...defaultValue, ...value };
    // 时间单位选项（支持更多单位）
    const timeUnits = [
        { value: 's', label: $i18n.get({ id: 'Seconds', dm: '秒' }) },
        { value: 'm', label: $i18n.get({ id: 'Minutes', dm: '分钟' }) },
        { value: 'h', label: $i18n.get({ id: 'Hours', dm: '小时' }) },
        { value: 'd', label: $i18n.get({ id: 'Day', dm: '天' }) },
        { value: 'w', label: $i18n.get({ id: 'Week', dm: '周' }) },
        { value: 'M', label: $i18n.get({ id: 'Month', dm: '月' }) },
        { value: 'Q', label: $i18n.get({ id: 'Quarter', dm: '季度' }) },
        { value: 'y', label: $i18n.get({ id: 'Year', dm: '年' }) },
    ];
    // 模式选项
    const modeOptions = [
        { value: 'now', label: $i18n.get({ id: 'Now', dm: '现在' }) },
        {
            value: 'relative',
            label: $i18n.get({ id: 'TimeLapse', dm: '时间推移' }),
        },
        {
            value: 'period',
            label: $i18n.get({ id: 'FixedPeriod', dm: '固定时段' }),
        },
    ];
    // 方向选项（更直观的表达）
    const directionOptions = [
        { value: 'subtract', label: $i18n.get({ id: 'Before.Lwu07', dm: '之前' }) },
        { value: 'add', label: $i18n.get({ id: 'After.jkqU3', dm: '之后' }) },
    ];
    // 时间段选项（扩展更多选项）
    const periodOptions = [
        { value: 'day', label: $i18n.get({ id: 'Today', dm: '今天' }) },
        { value: 'week', label: $i18n.get({ id: 'ThisWeek', dm: '本周' }) },
        { value: 'month', label: $i18n.get({ id: 'ThisMonth', dm: '本月' }) },
        { value: 'quarter', label: $i18n.get({ id: 'ThisQuarter', dm: '本季度' }) },
        { value: 'year', label: $i18n.get({ id: 'ThisYear', dm: '今年' }) },
    ];
    // 位置选项
    const positionOptions = [
        { value: 'start', label: $i18n.get({ id: 'StartTime', dm: '开始时间' }) },
        { value: 'end', label: $i18n.get({ id: 'EndTime', dm: '结束时间' }) },
        { value: 'now', label: $i18n.get({ id: 'Now', dm: '现在' }) },
    ];
    // 生成选项数组的工具函数
    const generateOptions = (max, padStart = true) => {
        return Array.from({ length: max }, (_, i) => {
            const optionValue = padStart
                ? i.toString().padStart(2, '0')
                : i.toString();
            return { value: optionValue, label: optionValue };
        });
    };
    // 小时选项 (0-23)
    const hourOptions = generateOptions(24);
    // 分钟和秒选项 (0-59)
    const minuteOptions = generateOptions(60);
    const secondOptions = generateOptions(60);
    // 处理值改变
    const handleChange = (field, newValue) => {
        const updatedValue = {
            ...currentValue,
            [field]: newValue,
        };
        // 根据模式调整可用选项
        if (field === 'mode') {
            if (newValue === 'period') {
                // 时间段模式下重置相对时间相关设置
                updatedValue.enableRounding = false;
                updatedValue.enableExactTime = false;
            }
            else if (newValue === 'now') {
                // 现在模式下重置所有设置
                updatedValue.enableRounding = false;
                updatedValue.enableExactTime = false;
            }
        }
        onChange?.(updatedValue);
    };
    // 生成时间表达式
    const generateExpression = () => {
        let expression = '';
        if (currentValue.mode === 'now') {
            // 现在模式
            expression = 'now()';
        }
        else if (currentValue.mode === 'relative') {
            // 相对时间模式
            if (currentValue.direction === 'subtract') {
                expression = `now()-${currentValue.amount}${currentValue.unit}`;
            }
            else {
                expression = `now()+${currentValue.amount}${currentValue.unit}`;
            }
            // 添加舍入
            if (currentValue.enableRounding && currentValue.amount) {
                const roundSuffix = currentValue.roundToStart === 'start' ? '' : '+';
                expression += `/${currentValue.unit}${roundSuffix}`;
            }
            // 添加精确时间
            if (currentValue.enableExactTime) {
                if (currentValue.second !== '00') {
                    expression += `@${currentValue.hour}:${currentValue.minute}:${currentValue.second}`;
                }
                else if (currentValue.minute !== '00') {
                    expression += `@${currentValue.hour}:${currentValue.minute}`;
                }
                else {
                    expression += `@${currentValue.hour}`;
                }
            }
        }
        else if (currentValue.mode === 'period') {
            // 时间段模式
            const periodMap = {
                day: 'd',
                week: 'w',
                month: 'M',
                quarter: 'Q',
                year: 'y',
            };
            const periodChar = periodMap[currentValue.period];
            if (currentValue.position === 'now') {
                expression = 'now()';
            }
            else if (currentValue.position === 'start') {
                expression = `now/${periodChar}`;
            }
            else if (currentValue.position === 'end') {
                expression = `now/${periodChar}+`;
            }
        }
        return expression;
    };
    // 生成友好的中文描述
    const generateDescription = () => {
        if (currentValue.mode === 'now') {
            return $i18n.get({ id: 'Now', dm: '现在' });
        }
        else if (currentValue.mode === 'relative') {
            const unitMap = {
                s: $i18n.get({ id: 'Seconds', dm: '秒' }),
                m: $i18n.get({ id: 'Minutes', dm: '分钟' }),
                h: $i18n.get({ id: 'Hours', dm: '小时' }),
                d: $i18n.get({ id: 'Day', dm: '天' }),
                w: $i18n.get({ id: 'Week', dm: '周' }),
                M: $i18n.get({ id: 'Months', dm: '个月' }),
                Q: $i18n.get({ id: 'Quarter.xeFPT', dm: '个季度' }),
                y: $i18n.get({ id: 'Year', dm: '年' }),
            };
            const unitText = unitMap[currentValue.unit] || currentValue.unit;
            const directionText = currentValue.direction === 'subtract'
                ? $i18n.get({ id: 'Before', dm: '前' })
                : $i18n.get({ id: 'After', dm: '后' });
            let desc = `${currentValue.amount}${unitText}${directionText}`;
            if (currentValue.enableRounding) {
                const roundText = currentValue.roundToStart === 'start'
                    ? $i18n.get({ id: 'Start', dm: '开始' })
                    : $i18n.get({ id: 'End', dm: '结束' });
                desc += $i18n.get({
                    id: 'FromCurrentValueAmountunitTextdi_371090681',
                    dm: ' (从{currentValueAmount}{unitText}{directionText}{roundText})',
                }, {
                    currentValueAmount: currentValue.amount,
                    unitText: unitText,
                    directionText: directionText,
                    roundText: roundText,
                });
            }
            if (currentValue.enableExactTime) {
                desc += ` ${currentValue.hour}:${currentValue.minute}:${currentValue.second}`;
            }
            return desc;
        }
        else if (currentValue.mode === 'period') {
            const periodMap = {
                day: $i18n.get({ id: 'Today', dm: '今天' }),
                week: $i18n.get({ id: 'ThisWeek', dm: '本周' }),
                month: $i18n.get({ id: 'ThisMonth', dm: '本月' }),
                quarter: $i18n.get({ id: 'ThisQuarter', dm: '本季度' }),
                year: $i18n.get({ id: 'ThisYear', dm: '今年' }),
            };
            const positionMap = {
                start: $i18n.get({ id: '0Point', dm: '0点' }),
                end: $i18n.get({ id: 'End', dm: '结束' }),
                now: $i18n.get({ id: 'Now', dm: '现在' }),
            };
            const periodText = periodMap[currentValue.period];
            const positionText = positionMap[currentValue.position];
            if (currentValue.position === 'now') {
                return $i18n.get({ id: 'Now', dm: '现在' });
            }
            else {
                return `${periodText}${positionText}`;
            }
        }
        return '';
    };
    // 生成舍入选项的动态标签
    const getRoundingLabel = () => {
        const unitMap = {
            s: $i18n.get({ id: 'Seconds', dm: '秒' }),
            m: $i18n.get({ id: 'Minutes', dm: '分钟' }),
            h: $i18n.get({ id: 'Hours', dm: '小时' }),
            d: $i18n.get({ id: 'Day', dm: '天' }),
            w: $i18n.get({ id: 'Week', dm: '周' }),
            M: $i18n.get({ id: 'Month', dm: '月' }),
            Q: $i18n.get({ id: 'Quarter', dm: '季度' }),
            y: $i18n.get({ id: 'Year', dm: '年' }),
        };
        const unitText = unitMap[currentValue.unit] || currentValue.unit;
        return $i18n.get({
            id: 'TimeAlignmentAdjustedToUnitTextB_1432841915',
            dm: '时间对齐 (调整到{unitText}边界)',
        }, { unitText: unitText });
    };
    // 生成舍入选项数据源
    const getRoundingOptions = () => {
        const unitOptionsMap = {
            s: {
                start: {
                    label: $i18n.get({ id: 'StartOfSecond00MS', dm: '秒开始 (00毫秒)' }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({ id: 'SecondEnd999MS', dm: '秒结束 (999毫秒)' }),
                    value: 'end',
                },
            },
            m: {
                start: {
                    label: $i18n.get({
                        id: 'MinuteStart00Seconds',
                        dm: '分钟开始 (00秒)',
                    }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({
                        id: 'EndOfMinute59Seconds',
                        dm: '分钟结束 (59秒)',
                    }),
                    value: 'end',
                },
            },
            h: {
                start: {
                    label: $i18n.get({ id: 'HourStart0000', dm: '小时开始 (00:00)' }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({ id: 'EndOfTheHour5959', dm: '小时结束 (59:59)' }),
                    value: 'end',
                },
            },
            d: {
                start: {
                    label: $i18n.get({ id: 'DayStart000000', dm: '天开始 (00:00:00)' }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({ id: 'EndOfDay235959', dm: '天结束 (23:59:59)' }),
                    value: 'end',
                },
            },
            w: {
                start: {
                    label: $i18n.get({
                        id: 'WeekStartsMonday000000',
                        dm: '周开始 (周一00:00:00)',
                    }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({
                        id: 'WeekEndsSunday235959',
                        dm: '周结束 (周日23:59:59)',
                    }),
                    value: 'end',
                },
            },
            M: {
                start: {
                    label: $i18n.get({
                        id: 'MonthStarts1st000000',
                        dm: '月开始 (1号00:00:00)',
                    }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({
                        id: 'EndOfTheMonthLastDay235959',
                        dm: '月结束 (最后一天23:59:59)',
                    }),
                    value: 'end',
                },
            },
            Q: {
                start: {
                    label: $i18n.get({
                        id: 'QuarterStartDay1000000',
                        dm: '季度开始 (第一天00:00:00)',
                    }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({
                        id: 'EndOfQuarterLastDay235959',
                        dm: '季度结束 (最后一天23:59:59)',
                    }),
                    value: 'end',
                },
            },
            y: {
                start: {
                    label: $i18n.get({
                        id: 'YearStartsJanuary1000000',
                        dm: '年开始 (1月1号00:00:00)',
                    }),
                    value: 'start',
                },
                end: {
                    label: $i18n.get({
                        id: 'YearEndDecember31235959',
                        dm: '年结束 (12月31号23:59:59)',
                    }),
                    value: 'end',
                },
            },
        };
        const options = unitOptionsMap[currentValue.unit];
        return options
            ? [options.start, options.end]
            : [
                {
                    label: $i18n.get({ id: 'StartTime.VqFDz', dm: '开始时刻' }),
                    value: 'start',
                },
                {
                    label: $i18n.get({ id: 'EndTime.VBuqb', dm: '结束时刻' }),
                    value: 'end',
                },
            ];
    };
    // 计算是否禁用某些字段
    const isNowMode = currentValue.mode === 'now';
    const isRelativeMode = currentValue.mode === 'relative';
    const isPeriodMode = currentValue.mode === 'period';
    const isAmountDisabled = disabled || isPeriodMode || isNowMode;
    const isRoundDisabled = disabled || isPeriodMode || isNowMode || !currentValue.amount;
    const isExactTimeDisabled = disabled || isPeriodMode || isNowMode;
    return (<div className="flexible-time-picker">
      {/* 标签 */}
      {label && (<div className="flexible-time-picker__label-wrapper">
          <label className="flexible-time-picker__label">{label}</label>
        </div>)}

      {/* 主要选择区域 */}
      <div className={`flexible-time-picker__main-grid flexible-time-picker__main-grid--${currentValue.mode}`}>
        <CnAsyncSelect size="small" value={currentValue.mode} onChange={(val) => handleChange('mode', val)} disabled={disabled} className="flexible-time-picker__type-select" dataSource={modeOptions}/>

        {isRelativeMode && (<>
            <CnAsyncSelect size="small" value={currentValue.direction} onChange={(val) => handleChange('direction', val)} disabled={disabled} className="flexible-time-picker__direction-select" dataSource={directionOptions}/>

            <CnNumberPicker size="small" min="0" value={currentValue.amount || ''} onChange={(val) => handleChange('amount', val)} disabled={isAmountDisabled} className="flexible-time-picker__amount-input"/>

            <CnAsyncSelect size="small" value={currentValue.unit} onChange={(val) => handleChange('unit', val)} disabled={isAmountDisabled} className="flexible-time-picker__unit-select" dataSource={timeUnits}/>
          </>)}

        {isPeriodMode && (<>
            <CnAsyncSelect size="small" value={currentValue.period} onChange={(val) => handleChange('period', val)} disabled={disabled} className="flexible-time-picker__period-select" dataSource={periodOptions}/>

            <CnAsyncSelect size="small" value={currentValue.position} onChange={(val) => handleChange('position', val)} disabled={disabled} className="flexible-time-picker__position-select" dataSource={positionOptions}/>
          </>)}
      </div>

      {/* 时间舍入选项 */}
      {isRelativeMode && (<div className="flexible-time-picker__section flexible-time-picker__section--round">
          <div className="flexible-time-picker__section-row">
            <div className="flexible-time-picker__checkbox-wrapper">
              <CnCheckbox checked={currentValue.enableRounding} onChange={(val) => handleChange('enableRounding', val)} disabled={isRoundDisabled}/>

              <span className="flexible-time-picker__section-label">
                {getRoundingLabel()}
              </span>
            </div>

            {currentValue.enableRounding && (<CnAsyncSelect value={currentValue.roundToStart} size="small" onChange={(val) => handleChange('roundToStart', val)} disabled={isRoundDisabled || !currentValue.enableRounding} className="flexible-time-picker__round-select" dataSource={getRoundingOptions()}/>)}
          </div>
        </div>)}

      {/* 指定具体时间选项 */}
      {isRelativeMode && (<div className="flexible-time-picker__section">
          <div className="flexible-time-picker__section-row">
            <div className="flexible-time-picker__checkbox-wrapper">
              <CnCheckbox checked={currentValue.enableExactTime} onChange={(val) => handleChange('enableExactTime', val)} disabled={isExactTimeDisabled}/>

              <span className="flexible-time-picker__section-label">
                {$i18n.get({ id: 'SpecifySpecificTime', dm: '指定具体时间' })}
              </span>
            </div>

            {currentValue.enableExactTime && (<div className="flexible-time-picker__time-inputs">
                <CnAsyncSelect value={currentValue.hour} size="small" onChange={(val) => handleChange('hour', val)} disabled={disabled} className="flexible-time-picker__time-select" dataSource={hourOptions}/>

                <CnAsyncSelect value={currentValue.minute} size="small" onChange={(val) => handleChange('minute', val)} disabled={disabled} className="flexible-time-picker__time-select" dataSource={minuteOptions}/>

                <CnAsyncSelect value={currentValue.second} size="small" onChange={(val) => handleChange('second', val)} disabled={disabled} className="flexible-time-picker__time-select" dataSource={secondOptions}/>
              </div>)}
          </div>
        </div>)}

      {/* 表达式预览 */}
      <div className="flexible-time-picker__expression-preview">
        <div className="flexible-time-picker__description-text">
          {generateDescription()}
        </div>
      </div>
    </div>);
};
export const FlexibleTimeSelection = (props) => {
    const { value, onChange } = props;
    // 默认值结构
    const defaultValue = {
        fromTime: {
            mode: 'relative',
            direction: 'subtract',
            amount: 2,
            unit: 'w',
            period: 'day',
            position: 'start',
            enableRounding: false,
            roundToStart: 'start',
            enableExactTime: false,
            hour: '09',
            minute: '30',
            second: '00',
        },
        toTime: {
            mode: 'now',
            direction: 'subtract',
            amount: '',
            unit: 'h',
            period: 'day',
            position: 'end',
            enableRounding: false,
            roundToStart: 'start',
            enableExactTime: false,
            hour: '00',
            minute: '00',
            second: '00',
        },
    };
    // 合并默认值和传入值
    const currentValue = React.useMemo(() => {
        if (!value || typeof value !== 'object') {
            return defaultValue;
        }
        return {
            fromTime: { ...defaultValue.fromTime, ...(value.fromTime || {}) },
            toTime: { ...defaultValue.toTime, ...(value.toTime || {}) },
        };
    }, [value]);
    // 处理开始时间变化
    const handleFromTimeChange = React.useCallback((newFromTime) => {
        const newValue = {
            ...currentValue,
            fromTime: newFromTime,
        };
        onChange?.(newValue);
    }, [currentValue, onChange]);
    // 处理结束时间变化
    const handleToTimeChange = React.useCallback((newToTime) => {
        const newValue = {
            ...currentValue,
            toTime: newToTime,
        };
        onChange?.(newValue);
    }, [currentValue, onChange]);
    // 生成时间表达式的工具函数
    const generateExpression = (timeValue) => {
        let expression = '';
        if (timeValue.mode === 'now') {
            expression = 'now()';
        }
        else if (timeValue.mode === 'relative') {
            if (timeValue.direction === 'subtract') {
                expression = `now()-${timeValue.amount}${timeValue.unit}`;
            }
            else {
                expression = `now()+${timeValue.amount}${timeValue.unit}`;
            }
            if (timeValue.enableRounding && timeValue.amount) {
                const roundSuffix = timeValue.roundToStart === 'start' ? '' : '+';
                expression += `/${timeValue.unit}${roundSuffix}`;
            }
            if (timeValue.enableExactTime) {
                if (timeValue.second !== '00') {
                    expression += `@${timeValue.hour}:${timeValue.minute}:${timeValue.second}`;
                }
                else if (timeValue.minute !== '00') {
                    expression += `@${timeValue.hour}:${timeValue.minute}`;
                }
                else {
                    expression += `@${timeValue.hour}`;
                }
            }
        }
        else if (timeValue.mode === 'period') {
            const periodMap = {
                day: 'd',
                week: 'w',
                month: 'M',
                quarter: 'Q',
                year: 'y',
            };
            const periodChar = periodMap[timeValue.period];
            if (timeValue.position === 'now') {
                expression = 'now()';
            }
            else if (timeValue.position === 'start') {
                expression = `now/${periodChar}`;
            }
            else if (timeValue.position === 'end') {
                expression = `now/${periodChar}+`;
            }
        }
        return expression;
    };
    // 如果初始化时 value 无值，自动设置默认值
    React.useEffect(() => {
        if ((!value || typeof value !== 'object') && onChange) {
            onChange(defaultValue);
        }
    }, []);
    // 生成完整的时间范围表达式
    const generateFullExpression = () => {
        const fromExpr = generateExpression(currentValue.fromTime);
        const toExpr = generateExpression(currentValue.toTime);
        return `${fromExpr} ~ ${toExpr}`;
    };
    return (<div className="flexible-time-selection__container">
      {/* 开始时间 */}

      <RelativeTimePicker label={$i18n.get({ id: 'StartTime', dm: '开始时间' })} value={currentValue.fromTime} onChange={handleFromTimeChange}/>

      <RelativeTimePicker label={$i18n.get({ id: 'EndTime', dm: '结束时间' })} value={currentValue.toTime} onChange={handleToTimeChange}/>
    </div>);
};
